// 대화·그룹·사용자 팝업 메뉴 — 바닐라 convMenu()(document.body 부착 fixed 메뉴) 이식.
// 앵커 아래(또는 place="above" 면 위) 좌측 정렬, 화면 밖이면 뷰포트 안으로 접어 넣는다.
// 밖 클릭·Escape·창 리사이즈 시 닫힘. 항목 클릭은 onPick(key) 로 위임.
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

export interface MenuEntry {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** 클릭해도 메뉴를 닫지 않는다 (링크 복사 피드백 등 — 호출측이 닫는다) */
  keepOpen?: boolean;
}

export type MenuItem = MenuEntry | "sep";

interface Props {
  anchor: HTMLElement;
  items: MenuItem[];
  /** 사용자 메뉴 상단 계정 표시줄 */
  header?: string;
  /** 하단 앵커(사용자 메뉴)는 위로 띄운다 */
  place?: "below" | "above";
  onPick: (key: string) => void;
  onClose: () => void;
}

export default function ConvMenu({ anchor, items, header, place, onPick, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // 위치: 메뉴 크기를 잰 뒤 앵커 기준으로 계산 (바닐라 openConvMenu 와 동일 규칙)
  useLayoutEffect(() => {
    const m = ref.current;
    if (!m) return;
    const r = anchor.getBoundingClientRect();
    const mw = m.offsetWidth, mh = m.offsetHeight, pad = 8;
    if (place === "above") {
      setPos({ left: Math.max(pad, r.left), top: Math.max(pad, r.top - mh - 8) });
      return;
    }
    const left = Math.max(pad, Math.min(r.left, window.innerWidth - mw - pad));
    let top = r.bottom + 6;
    if (top + mh > window.innerHeight - pad) top = Math.max(pad, r.top - mh - 6);
    setPos({ left, top });
  }, [anchor, place, items.length]);

  // 밖 클릭·Escape·리사이즈 → 닫기 (바닐라 document 위임과 동일)
  useLayoutEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      // 메뉴 내용 교체로 DOM 에서 떨어져나간 요소의 클릭은 '메뉴 밖'이 아니다
      if (!document.contains(t)) return;
      if (ref.current?.contains(t) || anchor.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // 여는 클릭 자체가 닫지 않도록 다음 틱에 바인딩
    const timer = setTimeout(() => document.addEventListener("click", onDoc), 0);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onClose);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onClose);
    };
  }, [anchor, onClose]);

  return (
    <div
      className="conv-menu open"
      role="menu"
      ref={ref}
      style={pos ? { left: pos.left, top: pos.top } : { visibility: "hidden" }}
    >
      {header != null && (
        <>
          <div className="user-menu-head">{header}</div>
          <div className="conv-menu-sep" />
        </>
      )}
      {items.map((it, i) =>
        it === "sep" ? (
          <div className="conv-menu-sep" key={`sep-${i}`} />
        ) : (
          <button
            type="button"
            role="menuitem"
            key={it.key}
            className={`${it.danger ? "danger" : ""}${it.disabled ? " disabled" : ""}`.trim() || undefined}
            disabled={it.disabled}
            title={it.disabled ? "준비 중" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              onPick(it.key);
            }}
          >
            {it.icon}
            {it.label}
          </button>
        ),
      )}
    </div>
  );
}
